import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

interface Props {
  open: boolean;
  clientId: number;
  prefill: string;
  onCompleted: () => void;
}

const MandatoryEmailNoteModal: React.FC<Props> = ({ open, clientId, prefill, onCompleted }) => {
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);
  const [body, setBody] = useState(prefill);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setBody(prefill);
  }, [open, prefill]);

  const handleSubmit = async () => {
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`/collections/clients/${clientId}/notes`, {
        note: { body: body.trim(), servman_type: 'COLLECTION' },
      });
      showSuccessSnackBar?.('Note saved.');
      onCompleted();
    } catch (error) {
      const err = error as { response?: { data?: { errors?: string[]; error?: string } } };
      showErrorSnackBar?.(err?.response?.data?.errors?.[0] || err?.response?.data?.error || 'Failed to save note.');
    } finally {
      setSubmitting(false);
    }
  };

  // Intentionally non-dismissable: no onClose handler, no escape, no backdrop close, no X/Cancel.
  return (
    <Dialog open={open} disableEscapeKeyDown maxWidth="sm" fullWidth>
      <DialogTitle>Record a note about this email</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          The email was sent. Add a note documenting it before finishing.
        </DialogContentText>
        <TextField
          value={body}
          onChange={(e) => setBody(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          maxRows={8}
          disabled={submitting}
          autoFocus
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!body.trim() || submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
        >
          Save note
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MandatoryEmailNoteModal;
